import { router } from 'expo-router';
import { capitalize } from 'lodash';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import TextInputField from '@/components/inputs/TextInputField';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import SaveAndContinueLaterButton from '@/components/npo/onboarding/SaveAndContinueLaterButton';
import { FundRaisingData, fundRaisingResolver } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';

interface NpoFundraisingFormProps {
  nonProfitOrg: NonProfitOrg | undefined;
  isLoading: boolean;
  showBackButton?: boolean;
  showSaveAndContinueButton?: boolean;
  submitLabel?: string;
  onSubmitTapped: (data: FundRaisingData) => void;
}

const NpoFundraisingForm = ({
  nonProfitOrg,
  isLoading,
  showBackButton = true,
  showSaveAndContinueButton = true,
  submitLabel = i18n.t('mix.next'),
  onSubmitTapped
}: NpoFundraisingFormProps) => {
  const initialData = {
    totalFundraisingMarketing: nonProfitOrg?.totalFundraisingMarketing
  };

  const {
    control,
    handleSubmit,
    formState: { isValid, submitCount }
  } = useForm<FundRaisingData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: fundRaisingResolver
  });

  const onSubmit: SubmitHandler<FundRaisingData> = async (data) => {
    onSubmitTapped(data);
  };

  return (
    <FormWrapper scrollEnable={false}>
      <View style={styles.container}>
        <View style={styles.fields}>
          <OnboardingSection
            label={i18n.t('sign_in.nonProfit.fundraising.title')}
            description={i18n.t('sign_in.nonProfit.fundraising.description')}>
            <TextInputField
              type="numeric"
              currency
              control={control}
              name="totalFundraisingMarketing"
              placeholder={i18n.t('sign_in.nonProfit.fundraising.placeholder')}
              returnKeyType="done"
            />
          </OnboardingSection>
        </View>

        <View style={styles.buttonsContainer}>
          <View style={styles.nextBackContainer}>
            {showBackButton && router.canGoBack() && (
              <BrandedButton disabled={isLoading} style={styles.flexOne} onPress={router.back}>
                {capitalize(i18n.t('mix.back'))}
              </BrandedButton>
            )}
            <BrandedButton
              style={styles.flexOne}
              loading={isLoading}
              disabled={!isValid && submitCount > 0}
              onPress={handleSubmit(onSubmit)}>
              {submitLabel}
            </BrandedButton>
          </View>
          {showSaveAndContinueButton && <SaveAndContinueLaterButton disabled={isLoading} />}
        </View>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  fields: {
    flexDirection: 'column',
    gap: 25
  },

  flexOne: {
    flex: 1
  },

  nextBackContainer: {
    flexDirection: 'row',
    width: '100%',
    gap: 10
  },

  buttonsContainer: {
    flexDirection: 'column',
    gap: 10
  }
});

export default NpoFundraisingForm;
